import {fetchWithMessages} from "./fetchWithMessages";
import {dayjsBase} from "@shared/services/dayjs";

export const AUTO_RELOAD_SECONDS = 10;

/**
 * @typedef {object} LoadListOptions
 * @property {boolean} [forceLoad] - If true, forces the reload even if data is fresh.
 * @property {boolean} [showLoading] - If true, shows the loading indicator if no data is present yet.
 * @property {function(): void} [notFoundAction] - Callback to execute if the entity is not found.
 */

/**
 * @typedef {object} LoadListInitData
 * @property {string} status - The loading status (e.g. 'idle', 'pending', 'loaded').
 * @property {string} [updatedAt] - The last update timestamp (ISO string).
 * @property {string} [project] - The project id associated with the data.
 */

/**
 * Loads a list of entities from the backend.
 *
 * @param {string} endpoint - The endpoint to load from
 * @param {string} [projectId] - The project id if relevant
 * @param {LoadListInitData} initData - The initial data state
 * @param {function(): void} initContextAction - Action to dispatch before loading (e.g. set loading state)
 * @param {function(data: object): void} initEntityAction - Action to dispatch with the loaded data
 * @param {LoadListOptions} [options] - Additional options
 */
export const loadListFromBackend = async (
  endpoint,
  projectId,
  initData,
  initContextAction,
  initEntityAction,
  {forceLoad = false, showLoading = true, notFoundAction} = {}
) => {
  // Reload if we are not already in a pending reload AND if we either:
  // - have no updatedAt date
  // - have an old updatedAt date
  // - have a different projectId than the current one
  if (
    forceLoad ||
    (initData.status !== "pending" &&
      (!initData.updatedAt ||
        dayjsBase().isAfter(dayjsBase(initData.updatedAt).add(AUTO_RELOAD_SECONDS, "second")) ||
        initData.project !== projectId))
  ) {
    // If we reload but the list was already loaded, don´t put back the context to "pending"
    // (prevent interface blinking when reload)
    if (initData.status !== "loaded") initContextAction?.();

    try {
      const data = await fetchWithMessages(
        projectId ? `projects/${projectId}/${endpoint}` : endpoint,
        {
          method: "GET",
          queryParams:
            initData.updatedAt && initData.project === projectId // Only do incremental if projectId is the same and there is a date
              ? {updatedAfter: initData.updatedAt, showDeletedElements: true}
              : undefined,
        },
        {showLoading: !initData.updatedAt && showLoading}
      );

      initEntityAction(data);
    } catch (e) {
      if (notFoundAction) {
        notFoundAction();
      } else {
        throw e;
      }
    }
  }
};
